import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import apiClient from '../api/client'
import { errorCode, errorMessage } from '../api/ApiError'
import { isAudio, isImage, isPdf, isVideo, type PreviewResponse } from './tipoDeDocumento'

/**
 * Ver un documento SIN salir del portal.
 *
 * El `Lightbox` vivía dentro de `DocumentPreview` y solo lo usaban Documentos y
 * Validar detección. La bandeja —donde el asesor pasa el día— abría cada
 * archivo en una pestaña nueva: el adjunto del chat y «Ver documento» del
 * panel hacían `window.open`. Reportado por el COE el 28-set-2026. Se mudó acá
 * para que los tres lugares usen el mismo visor, no una copia cada uno.
 *
 * `VisorDeDocumento` pide la URL firmada al abrirse —vence a los 5 minutos, así
 * que no se pide por adelantado— y muestra el `Lightbox`. «Abrir original»
 * sigue ahí, para descargarlo o verlo aparte cuando haga falta.
 *
 * Se monta en `document.body` (DEF-052, QA de TEST 05-oct-2026). Abierto desde
 * el panel del caso, quedaba DENTRO del panel: cuando la ventana cruzaba el
 * umbral de columna a cajón, el cajón arrancaba cerrado —`visibility: hidden`,
 * que se hereda— y el visor desaparecía con él. Con el portal no lo esconde ni
 * lo recorta ningún ancestro; y como el panel ya no se vuelve a montar al
 * cambiar de disposición (`PanelDelCaso`), el visor sigue abierto.
 */

/** Lo que se dibuja encima de todo, fuera del árbol de la página. */
function encimaDeTodo(contenido: ReactNode) {
  return createPortal(contenido, document.body)
}

export interface LightboxProps {
  preview: PreviewResponse
  onClose: () => void
}

export function Lightbox({ preview, onClose }: LightboxProps) {
  const [scale, setScale] = useState(1)
  const asImage = isImage(preview.mime_type, preview.filename)
  const asPdf = isPdf(preview.mime_type, preview.filename)
  const asAudio = !asImage && !asPdf && isAudio(preview.mime_type, preview.filename)
  const asVideo = !asImage && !asPdf && !asAudio && isVideo(preview.mime_type, preview.filename)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === '+' || e.key === '=') setScale((s) => Math.min(4, s + 0.25))
      if (e.key === '-') setScale((s) => Math.max(0.5, s - 0.25))
      if (e.key === '0') setScale(1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return encimaDeTodo(
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgb(13 21 32 / 0.85)',
        display: 'flex', flexDirection: 'column',
        fontFamily: "'Manrope', sans-serif",
      }}
    >
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 20px', background: 'var(--c-sidebar)', color: 'white',
      }}>
        <div style={{ fontSize: '13px', fontWeight: 700, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>
          {preview.filename}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {asImage && (
            <>
              <button onClick={() => setScale((s) => Math.max(0.5, s - 0.25))}
                style={btnStyle} aria-label="Reducir">−</button>
              <div style={{ fontSize: '12px', width: '46px', textAlign: 'center' }}>
                {Math.round(scale * 100)}%
              </div>
              <button onClick={() => setScale((s) => Math.min(4, s + 0.25))}
                style={btnStyle} aria-label="Ampliar">+</button>
              <button onClick={() => setScale(1)} style={btnStyle}>1:1</button>
            </>
          )}
          <a href={preview.url} target="_blank" rel="noreferrer" style={{ ...btnStyle, textDecoration: 'none' }}>
            Abrir original
          </a>
          <button onClick={onClose} style={btnStyle} aria-label="Cerrar">✕</button>
        </div>
      </div>

      {/* Contenido */}
      <div style={{
        flex: 1, overflow: 'auto', display: 'flex',
        alignItems: 'flex-start', justifyContent: 'center', padding: '20px',
      }}>
        {asImage && (
          <img
            src={preview.url}
            alt={preview.filename}
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'top center',
              maxWidth: '100%',
              height: 'auto',
              transition: 'transform 0.15s ease-out',
              boxShadow: '0 10px 40px rgb(13 21 32 / 0.5)',
            }}
          />
        )}
        {asPdf && (
          <iframe
            title={`Vista ampliada: ${preview.filename}`}
            src={preview.url}
            style={{
              width: 'min(920px, 100%)',
              height: '100%',
              minHeight: '80vh',
              border: 'none',
              background: 'white',
              boxShadow: '0 10px 40px rgb(13 21 32 / 0.5)',
            }}
          />
        )}
        {asAudio && (
          <audio controls autoPlay src={preview.url} style={{ width: 'min(520px, 100%)' }}>
            Tu navegador no puede reproducir este audio.
          </audio>
        )}
        {asVideo && (
          <video controls autoPlay src={preview.url}
                 style={{ maxWidth: 'min(920px, 100%)', maxHeight: '80vh', background: 'black' }}>
            Tu navegador no puede reproducir este video.
          </video>
        )}
        {!asImage && !asPdf && !asAudio && !asVideo && (
          <div style={{ color: 'white' }}>
            <a href={preview.url} target="_blank" rel="noreferrer"
               style={{ color: 'var(--c-info-border)', fontWeight: 700 }}>
              Descargar {preview.filename}
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

const btnStyle: CSSProperties = {
  all: 'unset', cursor: 'pointer',
  background: 'var(--c-sidebar-active)', color: 'white',
  padding: '6px 12px', borderRadius: '8px',
  fontSize: '12px', fontWeight: 700,
  minWidth: '32px', textAlign: 'center',
}

/**
 * Abre el visor sobre un documento por su id: pide la URL al montarse y
 * muestra el mismo `Lightbox`, con los estados de carga y de error adentro, de
 * modo que el clic tenga respuesta inmediata aunque S3 tarde.
 */
export function VisorDeDocumento({ documentId, filename, onClose }: {
  documentId: string
  filename: string
  onClose: () => void
}) {
  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    setPreview(null)
    setError(null)
    void apiClient.get<PreviewResponse>(`/documents/${encodeURIComponent(documentId)}/preview`)
      .then((r) => {
        if (!vivo) return
        if (r.data.url) setPreview({ ...r.data, filename: r.data.filename || filename })
        else setError('El documento no tiene archivo asociado.')
      })
      .catch((err: unknown) => {
        if (!vivo) return
        setError(errorCode(err) === 'archivo_ausente'
          ? 'El archivo ya no está: el registro sigue, pero el original se borró del almacén.'
          : errorMessage(err, 'No se pudo abrir el documento.'))
      })
    return () => { vivo = false }
  }, [documentId, filename])

  useEffect(() => {
    if (preview) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [preview, onClose])

  if (preview) return <Lightbox preview={preview} onClose={onClose} />

  return encimaDeTodo(
    <div role="dialog" aria-modal="true" onClick={onClose}
         style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgb(13 21 32 / 0.85)',
                  display: 'grid', placeItems: 'center', fontFamily: "'Manrope', sans-serif" }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: 'white', borderRadius: '12px', padding: '18px 22px',
                    maxWidth: '420px', textAlign: 'center' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--c-ink)' }}>{filename}</div>
        <div style={{ fontSize: '12px', marginTop: '6px',
                      color: error ? 'var(--c-danger)' : 'var(--c-ink-muted)' }}>
          {error ?? 'Abriendo el documento…'}
        </div>
        {error && (
          <button onClick={onClose} style={{ ...btnStyle, marginTop: '12px' }}>Cerrar</button>
        )}
      </div>
    </div>
  )
}
